// Package imports:
import React, { useMemo, useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
// Component imports:
import Input from '../../../ui-elements/Input/Formik/Input';
import Button from '../../../ui-elements/Button/Button';
import Alert from '../../../ui-elements/Alert/Alert';
// Service imports:
import { GET_KELDAN_API_URL } from '../../../services/config';
import { isCompany, isPerson } from '../../../services/utils';
import { ErrorMessages } from '../../../services/errorMessages';
// Type imports:
import { Fetched } from '../../../types/Types';
import { IKeldanApiContact } from '../../../types/KeldanTypes';

const GEMMAQContactForm: React.FC = () => {
    const [loading, setLoading] = useState<boolean>(false)
    const [contactResponse, setContactResponse] = useState<Fetched<string>>(null);

    const handleSubmit = async (values: IFormValues, actions: any) => {
        setLoading(true)
        try {
            setContactResponse(null);
            const requestQuery = new URLSearchParams({
                company_name: values.company,
                company_ssn: values.companySSN,
                contact_name: values.name,
                contact_ssn: values.contactSSN,
                contact_email: values.email,
            });
            const url = `${GET_KELDAN_API_URL()}/Market/OrderGenderLens`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                },
                body: requestQuery
            });
            if (response.ok) {
                const responseBody: IKeldanApiContact = await response.json();
                setLoading(false)
                if (responseBody.success) {
                    setContactResponse(responseBody?.message || 'Fyrirspurn móttekin.');
                } else {
                    const { errors, message } = responseBody;
                    if (errors && errors.length > 0) {
                        setContactResponse(new Error(errors.join('\n')));
                    } else if (message) {
                        setContactResponse(new Error(message));
                    } else {
                        setContactResponse(new Error(ErrorMessages.ErrorInRequest));
                    }
                }
            } else {
                setLoading(false);
                setContactResponse(new Error(ErrorMessages.RequestFailed));
            }
        } catch (e) {
            setLoading(false);
            setContactResponse(new Error(ErrorMessages.NetworkError));
        }
    }

    const subFormComponent = useMemo(() => {
        return ((props: { values: IFormValues }) => (
            <SubFormComponent
                contactResponse={contactResponse}
                values={props.values}
                loading={loading}
            />
        ))
    }, [ contactResponse, loading ]);

    return (
        <div className='KCL_contact-form'>
            <Formik
                initialValues={{
                    company: '',
                    companySSN: '',
                    name: '',
                    contactSSN: '',
                    email: '',
                }}
                validationSchema={Yup.object({
                    company: Yup.string()
                        .required('Nafn fyrirtækis verður að fylgja'),
                    companySSN: Yup.string()
                        .required('Kennitala fyrirtækis verður að fylgja')
                        .matches(/^([\d]{6}[-][\d]{4})|^(\d+)$/, 'Kennitala má einungis innihalda tölustafi')
                        .min(10, 'Kennitala þarf að innihalda 10 tölustafi')
                        .max(11, 'Kennitala má ekki vera meira en 10 tölustafir')
                        .test('ogild-kennitala', 'Ógild fyrirtækja kennitala', (value) => isCompany(value)),
                    name: Yup.string()
                        .required('Nafn tengiliðs verður að fylgja'),
                    contactSSN: Yup.string()
                        .required('Kennitala tengiliðs verður að fylgja')
                        .matches(/^([\d]{6}[-][\d]{4})|^(\d+)$/, 'Kennitala má einungis innihalda tölustafi')
                        .min(10, 'Kennitala þarf að innihalda 10 tölustafi')
                        .max(11, 'Kennitala má ekki vera meira en 10 tölustafir')
                        .test('ogild-kennitala', 'Ógild einstaklings kennitala', (value) => isPerson(value)),
                    email: Yup.string()
                        .email('Ógilt netfang')
                        .required('Netfang verður að fylgja'),
                })}
                onSubmit={handleSubmit}
                component={subFormComponent}
            />
        </div>
    );
}

export default GEMMAQContactForm;

interface IFormValues {
    company: string,
    companySSN: string,
    name: string,
    contactSSN: string,
    email: string,
}


interface ISubFormProps {
    contactResponse: Fetched<string>
    values: IFormValues,
    loading: boolean,
}

const SubFormComponent: React.FC<ISubFormProps> = ({
    contactResponse,
    values,
    loading,
}) => {
    return (
        <Form>
            <div className="form__body">
                <div className="form__section">
                    <h3>Panta notkunarleyfi</h3>
                    <div className="form__row">
                        <div className='form__col'>
                            <Input
                                label="Fyrirtæki"
                                name="company"
                                id="company"
                                value={values.company}
                            />
                        </div>
                        <div className='form__col'>
                            <Input
                                label="Kennitala"
                                name="companySSN"
                                id="companySSN"
                                value={values.companySSN}
                            />
                        </div>
                    </div>
                </div>

                <h4>Tengiliður fyrirtækis</h4>
                <div className="form__row" style={{marginBottom: "20px"}}>
                    <div className='form__col'>
                        <Input
                            label="Nafn"
                            name="name"
                            id="name"
                            value={values.name}
                        />
                    </div>
                    <div className='form__col'>
                        <Input
                            label="Kennitala"
                            name="contactSSN"
                            id="contactSSN"
                            value={values.contactSSN}
                        />
                    </div>
                </div>
                <div className='form__row'>
                    <div className='form__col'>
                        <Input
                            label="Netfang"
                            name="email"
                            id="email"
                            value={values.email}
                        />
                    </div>
                    <div className='form__col'></div>
                </div>


                {(contactResponse === null)
                    ? null
                    : (
                        <div className="form__row">
                            <div className="form__col">
                                {(contactResponse instanceof Error)
                                ? <Alert type='error' headText={contactResponse.message} />
                                : <Alert type='info' headText={contactResponse} />}
                            </div>
                        </div>
                    )
                }
                <div className="form__actions">
                    <Button showLoader={loading} buttonType="primary" size="lg">Senda pöntun</Button>
                </div>
            </div>
        </Form>
    )
}
